/**
 * answerText.js
 * -------------
 * A person's answers as plain printable words (TDI-692): what "Download your
 * answers" prints for a SENCO or a parent or carer. Every answer reads in the
 * question's own words: option labels, the labelled parts of a name or a
 * nomination, measurements with their units and "When was this taken?", and
 * milestones in words. A raw field name never prints; a value this file does
 * not recognise prints "See the form" rather than its keys.
 *
 * Pure: schema questions and saved answers in, strings out.
 */

import { isConditionalMet, isQuestionVisible, isSectionVisibleForRole } from './schemaEvaluator';

export const NO_ANSWER = 'No answer given';
export const SEE_THE_FORM = 'See the form';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const isEmpty = (v) =>
  v === null || v === undefined || v === ''
  || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

/** The copy of an email or phone number typed twice to check it: never printed. */
const isConfirmCopy = (child) =>
  child.type === 'email_confirm' || child.type === 'phone_confirm' || /_confirm$/.test(child.questionId || '');

/** "17 July 2026" from an ISO date, as the form showed it. */
function longDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d || m > 12) return null;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** One chosen option in words, with any "Other, tell us more" text. */
function optionText(question, raw) {
  const id = raw && typeof raw === 'object' ? raw.optionId : raw;
  const match = (question.options || []).find((o) => o.optionId === id);
  if (!match) {
    if (raw && typeof raw === 'object' && raw.label) return String(raw.label);
    return typeof id === 'string' || typeof id === 'number' ? String(id) : SEE_THE_FORM;
  }
  const extra = raw && typeof raw === 'object' && raw.otherText;
  return extra ? `${match.label}: ${extra}` : match.label;
}

/** A single part's value (a nomination field, a group child, a list cell). */
function partText(part, value) {
  if (isEmpty(value)) return null;
  if (part.type === 'date') return longDate(value) || String(value);
  if ((part.options || []).length) {
    return Array.isArray(value) ? value.map((v) => optionText(part, v)).join(', ') : optionText(part, value);
  }
  if (value && typeof value === 'object') {
    if (value.label) return String(value.label);
    return SEE_THE_FORM;
  }
  return String(value);
}

/** "First Name: Amy. Last Name: Penhallow. Email: amy@example.com" */
function labelledParts(children, source) {
  return (children || [])
    .filter((child) => !isConfirmCopy(child))
    .map((child) => {
      const text = partText(child, source ? source[child.questionId] : undefined);
      return text === null ? null : `${child.label}: ${text}`;
    })
    .filter(Boolean)
    .join('. ');
}

/** "When was this taken? March 2026" (or "Not sure"), as the form asks it. */
function whenTaken(entry) {
  if (!entry) return null;
  if (entry.dateNotSure) return 'When was this taken? Not sure';
  const month = Number(entry.date && entry.date.month);
  const year = Number(entry.date && entry.date.year);
  if (month >= 1 && month <= 12 && year) return `When was this taken? ${MONTHS[month - 1]} ${year}`;
  return null;
}

const hasNumber = (n) => n !== null && n !== undefined && n !== '' && Number.isFinite(Number(n));

function measurementText(question, v) {
  if (!v || typeof v !== 'object') return null;
  const lines = [];
  const withDate = (text, entry) => [text, whenTaken(entry)].filter(Boolean).join('. ');
  switch (question.measurementType) {
    case 'height_weight': {
      const h = v.height || {};
      const w = v.weight || {};
      if (h.unit === 'ft_in' && (hasNumber(h.ft) || hasNumber(h.in))) {
        lines.push(withDate(`Height: ${Number(h.ft) || 0} ft ${Number(h.in) || 0} in`, h));
      } else if (hasNumber(h.value)) {
        lines.push(withDate(`Height: ${h.value} ${h.unit || 'cm'}`, h));
      }
      if (hasNumber(w.value)) lines.push(withDate(`Weight: ${w.value} ${w.unit || 'kg'}`, w));
      break;
    }
    case 'head_circumference':
      if (v.notSure) lines.push("I don't know");
      else if (hasNumber(v.value)) lines.push(withDate(`${v.value} ${v.unit || 'cm'}`, v));
      break;
    case 'pulse_rate':
      if (v.unable) lines.push("I'm unable to take this measurement");
      else if (hasNumber(v.value)) lines.push(withDate(`${v.value} bpm`, v));
      break;
    case 'blood_pressure':
      if (v.unable) lines.push("I'm unable to take this measurement");
      else if (hasNumber(v.systolic) || hasNumber(v.diastolic)) {
        lines.push(withDate(`${hasNumber(v.systolic) ? v.systolic : '-'}/${hasNumber(v.diastolic) ? v.diastolic : '-'} mmHg`, v));
      }
      break;
    default:
      return SEE_THE_FORM;
  }
  return lines.length ? lines.join('\n') : null;
}

function milestoneText(question, v) {
  if (!v || typeof v !== 'object') return null;
  const lines = (question.milestones || []).map((m) => {
    const entry = v[m.milestoneId];
    if (!entry) return null;
    if (entry.notYetDoing) return `${m.label}: Not yet doing`;
    if (entry.notSure) return `${m.label}: Not sure`;
    if (hasNumber(entry.age)) return `${m.label}: ${entry.age} ${entry.unit || 'months'}`;
    return null;
  }).filter(Boolean);
  return lines.length ? lines.join('\n') : null;
}

/**
 * One question's answer as printable text. `answers` is the whole form's
 * answers, because a group keeps its parts' answers beside it, not inside.
 */
export function answerText(question, answers = {}, context = null) {
  const value = answers[question.questionId];
  switch (question.type) {
    case 'group': {
      const children = (question.children || []).filter(
        (child) => !context || isQuestionVisible(answers, child.visibleWhen, context)
      );
      return labelledParts(children, answers) || NO_ANSWER;
    }
    case 'nomination': {
      const people = Array.isArray(value) ? value : (value && typeof value === 'object' ? [value] : []);
      const lines = people.map((person) => labelledParts(question.children, person)).filter(Boolean);
      if (!lines.length) return NO_ANSWER;
      return lines.length === 1 ? lines[0] : lines.map((line, i) => `${i + 1}. ${line}`).join('\n');
    }
    case 'repeatable_group': {
      const items = Array.isArray(value) ? value : [];
      if (!items.length) return NO_ANSWER;
      return items
        .map((item, i) => {
          const parts = (question.children || question.fields || [])
            .filter((child) => !isConfirmCopy(child))
            .map((child) => {
              const text = partText(child, item && item[child.questionId]);
              return text === null ? null : `${child.label}: ${text}`;
            })
            .filter(Boolean);
          return `${i + 1}. ${parts.join('; ') || '-'}`;
        })
        .join('\n');
    }
    case 'physicalHealthInfoGroup':
      return measurementText(question, value) || NO_ANSWER;
    case 'milestoneAgeGroup':
      return milestoneText(question, value) || NO_ANSWER;
    case 'date':
      if (isEmpty(value)) return NO_ANSWER;
      return longDate(value) || String(value);
    case 'address': {
      const parts = [value && value.addr_street, value && value.addr_city, value && value.addr_postcode].filter(Boolean);
      return parts.length ? parts.join(', ') : NO_ANSWER;
    }
    case 'schoolSelect': {
      if (!value || !value.schoolName) return NO_ANSWER;
      const addr = value.schoolAddress || {};
      const line = [addr.line1, addr.city, addr.postcode].filter(Boolean).join(', ');
      return line ? `${value.schoolName} (${line})` : value.schoolName;
    }
    default:
      break;
  }
  if (isEmpty(value)) return NO_ANSWER;
  if (Array.isArray(value)) return value.map((v) => optionText(question, v)).join(', ');
  if ((question.options || []).length) return optionText(question, value);
  if (typeof value === 'object') return SEE_THE_FORM;
  return String(value);
}

/**
 * One form, as its respondent was shown it, ready to print: the sections the
 * role sees, and the questions whose conditions were met, each with its
 * answer in words. Sections with nothing to show are left out.
 */
export function printableSections(bundle, role) {
  const schema = (bundle && bundle.schema) || {};
  const answers = (bundle && bundle.answers) || {};
  const initiatedBy = (bundle && bundle.context && bundle.context.initiatedBy) || 'school';
  const context = { pathway: 'cyp', initiatedBy, currentUser: { role } };
  return (schema.sections || [])
    .filter((section) => isSectionVisibleForRole(section, role, initiatedBy))
    .map((section) => ({
      title: section.title || '',
      questions: (section.questions || [])
        .filter((q) => q.type !== 'info'
          && isQuestionVisible(answers, q.visibleWhen, context)
          && isConditionalMet(answers, q))
        .map((q) => ({ id: q.questionId, label: q.label, answer: answerText(q, answers, context) })),
    }))
    .filter((section) => section.questions.length > 0);
}
